/**
 * User Domain Model
 * Represents a user in the system with role-based access control
 */

export class User {
  constructor(data) {
    this.id = data.id;
    this.email = data.email;
    this.name = data.name || '';
    this.role = data.role || 'student'; // admin, course_manager, student
    this.enrolledCourses = data.enrolledCourses || []; // Courses user is enrolled in (for students)
    this.managedCourses = data.managedCourses || []; // Courses user manages (for course managers)
    this.isActive = data.isActive !== undefined ? data.isActive : true;
    this.createdAt = data.createdAt || new Date().toISOString();
    this.updatedAt = data.updatedAt || new Date().toISOString();
    this.lastLoginAt = data.lastLoginAt || null;
    this.avatar = data.avatar || null;
    this.bio = data.bio || '';
    this.passwordHash = data.passwordHash || null; // For authentication
  }

  /**
   * Check if user is enrolled in a course
   */
  isEnrolledIn(courseId) {
    return this.enrolledCourses.includes(courseId);
  }

  /**
   * Enroll user in a course
   */
  enroll(courseId) {
    if (!this.isEnrolledIn(courseId)) {
      this.enrolledCourses.push(courseId);
      this.updatedAt = new Date().toISOString();
    }
  }

  /**
   * Unenroll user from a course
   */
  unenroll(courseId) {
    const index = this.enrolledCourses.indexOf(courseId);
    if (index > -1) {
      this.enrolledCourses.splice(index, 1);
      this.updatedAt = new Date().toISOString();
    }
  }

  /**
   * Check if user has admin privileges
   */
  isAdmin() {
    return this.role === 'admin';
  }

  /**
   * Check if user has course manager privileges
   */
  isCourseManager() {
    return this.role === 'course_manager' || this.role === 'admin';
  }

  /**
   * Update user profile
   */
  updateProfile(updates) {
    const allowedFields = ['name', 'email', 'bio', 'avatar'];
    allowedFields.forEach(field => {
      if (updates[field] !== undefined) {
        this[field] = updates[field];
      }
    });
    this.updatedAt = new Date().toISOString();
  }

  /**
   * Update last login time
   */
  updateLastLogin() {
    this.lastLoginAt = new Date().toISOString();
  }

  /**
   * Convert to JSON
   */
  toJSON() {
    return {
      id: this.id,
      email: this.email,
      name: this.name,
      role: this.role,
      enrolledCourses: this.enrolledCourses,
      managedCourses: this.managedCourses,
      isActive: this.isActive,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      lastLoginAt: this.lastLoginAt,
      avatar: this.avatar,
      bio: this.bio,
      passwordHash: this.passwordHash,
    };
  }

  /**
   * Create from JSON
   */
  static fromJSON(json) {
    return new User(json);
  }
}
